import { Route } from 'react-router-dom';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { LegacyLineagePage } from '@developer-portal/legacy-lineage';
import { CustomTechDocsHome } from './components/techdocs/CustomTechDocsHome';
import { CustomApidocsPage } from './components/api-docs/CustomApidocsPage';
import { rdcThemeDark, rdcThemeLight } from './theme/rdc';
import { CustomSignInPage } from './components/layout/CustomSignInPage';
import {
  DrEventTaxonomyReferenceDataPage,
  HealthDashboard,
  EventComponent,
  ParamComponent,
  EventsComponent,
  GlobalsHomePage,
  GlobalsComponent,
} from '@developer-portal/plugin-dr-event-taxonomy-reference-data';
import { OpsgeniePage } from '@moveinc/backstage-plugin-opsgenie';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ExpandableNavigation } from '@backstage/plugin-techdocs-module-addons-contrib';
import { DevportalOrgHubPage } from '@developer-portal/plugin-devportal-org-hub';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SelectUsersFieldExtension } from './scaffolder/SelectUsers';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { UnregisteredReposPage } from '@developer-portal/plugin-unregistered-repos';
import { EntityValidationPage } from '@backstage-community/plugin-entity-validation';
import { GleanSearchPage, GleanTechDocsSearch } from '@moveinc/backstage-plugin-glean';
import { techDocsPage } from './components';
import { NotificationsPage, UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppRoot,
  convertLegacyAppOptions,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint, ThemeBlueprint } from '@backstage/plugin-app-react';
import { UploadFilesExtension } from './scaffolder/UploadFiles';
import { dxPlugin } from '@get-dx/backstage-plugin';
import { createCatalogPageColumns } from './lib/CatalogPageColumnsHelper';
import {
  MdgpDashboardPage,
  DomainSystemsComponent,
  IncompleteRequestsComponent,
} from '@developer-portal/backstage-plugin-mdgp-dashboard';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  EntityKindPicker,
  EntityTypePicker,
  UserListPicker,
  EntityOwnerPicker,
  EntityLifecyclePicker,
  EntityTagPicker,
  EntityProcessingStatusPicker,
  EntityNamespacePicker,
} from '@backstage/plugin-catalog-react';
import { EntityTierPicker } from './components/catalog/custom-pickers/EntityTierPicker';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { techRadarPluginOverrides, catalogGraphPluginOverrides } from './extensions';

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={createCatalogPageColumns}
          filters={
            <>
              <EntityKindPicker />
              <EntityTypePicker />
              <UserListPicker />
              <EntityTierPicker />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
              <EntityNamespacePicker />
            </>
          }
        />
      }
    />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <CustomTechDocsHome />
    </Route>
    <Route path="/tech-radar" element={<TechRadarPage width={1500} height={800} />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <GleanTechDocsSearch />
        <ExpandableNavigation />
      </TechDocsAddons>
      {techDocsPage}
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{
            pageTitleOverride: 'RDC Templates',
            title: `RDC Templates`,
            subtitle: '',
          }}
          // Hide templates that are Ad-hoc jobs
          templateFilter={entity => entity.spec.type !== 'job'}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <SelectUsersFieldExtension />
        <UploadFilesExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />}>
      <CustomApidocsPage />
    </Route>
    <Route path="/catalog-import" element={<CatalogImportPage />} />
    {/* Load Glean search routes in the App root so SearchPage can render them */}
    <Route path="/glean-search" element={<GleanSearchPage />} />
    <Route path="/search" element={<SearchPage />}>
      <GleanSearchPage />
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{
            'plugin:scaffolder': 'Scaffolder',
            'plugin:techdocs': 'TechDocs',
            'plugin:catalog': 'Catalog',
            'plugin:qeta': 'Q&A',
          }}
        />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/qeta" element={<QetaPage title="Q & A" />} />
    <Route path="/legacy-lineage" element={<LegacyLineagePage />} />
    <Route path="/dr-event-taxonomy-reference-data" element={<DrEventTaxonomyReferenceDataPage />} />
    <Route path="/dr-event-taxonomy-reference-data/dashboard" element={<HealthDashboard />} />
    <Route path="/dr-event-taxonomy-reference-data/events" element={<EventsComponent />} />
    <Route path="/dr-event-taxonomy-reference-data/events/:eventID" element={<EventComponent />} />
    <Route path="/dr-event-taxonomy-reference-data/events/:eventID/:paramID" element={<ParamComponent />} />
    <Route path="/dr-event-taxonomy-reference-data/globals" element={<GlobalsHomePage />} />
    <Route path="/dr-event-taxonomy-reference-data/globals/:platformID" element={<GlobalsComponent />} />
    <Route path="/opsgenie" element={<OpsgeniePage />} />
    <Route path="/devportal-org-hub" element={<DevportalOrgHubPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route path="/catalog-unprocessed-entities" element={<CatalogUnprocessedEntitiesPage />} />
    <Route path="/entity-validation" element={<EntityValidationPage hideFileLocationField />} />
    <Route path="/unregistered-repos" element={<UnregisteredReposPage />} />
    <Route path="/mdgp-dashboard" element={<MdgpDashboardPage />} />
    <Route path="/mdgp-dashboard/domain-systems" element={<DomainSystemsComponent />} />
    <Route path="/mdgp-dashboard/incomplete-requests" element={<IncompleteRequestsComponent />} />
  </FlatRoutes>
);

const legacyFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const optionsModule = convertLegacyAppOptions({
  plugins: [dxPlugin],
  featureFlags: [
    {
      pluginId: '',
      name: 'show-get-dx',
      description: 'Enables the getDX plugin',
    },
  ],
});

const proxiedSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => <CustomSignInPage {...props} />,
  },
});

const lightThemeExtension = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => <UnifiedThemeProvider theme={rdcThemeLight} children={children} />,
    },
  },
});

const darkThemeExtension = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => <UnifiedThemeProvider theme={rdcThemeDark} children={children} />,
    },
  },
});

const app = createApp({
  features: [
    optionsModule,
    ...legacyFeatures,
    techRadarPluginOverrides,
    catalogGraphPluginOverrides,
    createFrontendModule({
      pluginId: 'app',
      extensions: [...apis, proxiedSignInPage, lightThemeExtension, darkThemeExtension],
    }),
  ],
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(scaffolderPlugin.routes.selectedTemplate),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();
